import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import {
  CompanyDriverMetric,
  DriverCurrentTier,
  DriverMetricDetail,
  TierSimulationExplanation,
} from '../../../../types';
import { CompanyService } from '../../../company.service';

@Component({
  selector: 'lohi-driver-tier-metric-detail',
  templateUrl: './driver-tier-metric-detail.component.html',
  styleUrls: ['./driver-tier-metric-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverTierMetricDetailComponent implements OnInit, OnDestroy {
  public loading$$ = new BehaviorSubject(false);

  @Input() public receivedData$: BehaviorSubject<DriverCurrentTier>;
  public driverMetricDetail$$ = new BehaviorSubject<DriverMetricDetail>(null);
  private subscription = new Subscription();
  public displayedColumns = ['name', 'sumPoints', 'eventCount'];
  private destroy$$ = new Subject();
  public companyDriverMetrics$: Observable<CompanyDriverMetric>;
  public filteredTierDrivers$$ = new ReplaySubject(1);
  public selectedDriverControl = new FormControl('');
  public companyDriverSearchText = new FormControl('');

  public currentTierSimulationExplanation$$ = new BehaviorSubject<TierSimulationExplanation>(null);

  public hitchLength: number;
  public turnRate: number;
  public nightShift: string;
  private currentUserID: string;

  get driverMetricDetail$(): Observable<DriverMetricDetail> {
    return this.driverMetricDetail$$.asObservable();
  }

  constructor(private companyService: CompanyService) {}

  public ngOnInit(): void {
    this.companyDriverMetrics$ = this.companyService.companyDriverMetrics$;
    combineLatest([this.companyDriverMetrics$, this.companyDriverSearchText.valueChanges.pipe(startWith(''))]).
    pipe(
      map(([companyDrivers, searchText]) => {
        const drivers = companyDrivers?.driverCurrentTier || [];
        if (!searchText) {
          return drivers;
        }
        return drivers.filter((driver) => driver.name.toLowerCase().includes(searchText.toLowerCase()));
      }),
      takeUntil(this.destroy$$),
    ).subscribe((drivers) => {
        const newDrivers = [...drivers];
        this.filteredTierDrivers$$.next(newDrivers.sort((a, b) => a.name.localeCompare(b.name)));
      }
    );

    this.receivedData$.subscribe((data) => {
      if (data?.userID && this.currentUserID !== data?.userID) {
        this.selectedDriverControl.setValue(data);
        this.loading$$.next(true);
        this.companyService.getDriverMetricDetails(data?.userID).then((details) => {
          this.turnRate = details?.metricDetail?.waysToBlackTier?.simulationExplanations?.[0]?.turnRate;
          this.hitchLength = details?.metricDetail?.waysToBlackTier?.simulationExplanations?.[0]?.hitchLength;
          this.nightShift = details?.metricDetail?.waysToBlackTier?.simulationExplanations?.[0]?.isNightShift
            ? 'true'
            : 'false';
          this.driverMetricDetail$$.next(details);
          this.loading$$.next(false);
          this.currentUserID = data?.userID;
        });
      }
    });
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public selectCompanyDriver(data: DriverCurrentTier) {
    this.receivedData$.next(data);
  }

  public setTurnRate(newValue: number) {
    this.turnRate = newValue;
    this.setSummary();
  }

  public setHitchLength(newValue: number) {
    this.hitchLength = newValue;
    this.setSummary();
  }

  public setNightShift(newValue: string) {
    if (newValue === undefined) {
      return;
    }
    this.nightShift = newValue;
    this.setSummary();
  }

  public async setSummary() {
    const details = await this.driverMetricDetail$$.pipe(take(1)).toPromise();
    if (!details) {
      return;
    }

    const isNightShift = this.nightShift === 'true';

    for (const explanation of details?.metricDetail?.waysToBlackTier?.simulationExplanations) {
      if (
        explanation.turnRate === this.turnRate &&
        explanation.hitchLength === this.hitchLength &&
        explanation.isNightShift === isNightShift
      ) {
        this.currentTierSimulationExplanation$$.next(explanation);
        break;
      }
    }
  }
}
